// ── cohort-invites-collection: per-session invite log adapter for invites tab ─

import { useMemo } from "react";
import { Badge, useCollection, type CollectionColumnDef, type CollectionFilterDef, type UseCollectionResult } from "@vjal/ui";

import type { CoachingSessionDto } from "../../../services/coaching-dtos";
import type { CoachingInviteLogEntry } from "../../../services/coaching-z6-dtos";
import {
  buildCohortSessionFilterDefs,
  cohortSessionFilterInitialState,
  COHORT_TAB_PAGE_SIZE,
  type SessionScopedRow
} from "./cohort-tab-collection-shared";

export type InviteRow = {
  id: string;
  session_id: string;
  attempt: string;
  channel: string;
  outcome: string;
  actor: string;
  createdAt: string;
} & Record<string, unknown>;

export function mapInviteEntries(entries: CoachingInviteLogEntry[], sessionId: string): InviteRow[] {
  return entries.map((entry) => ({
    id: entry.id,
    session_id: sessionId,
    attempt: `#${entry.attempt}`,
    channel: entry.channel,
    outcome: entry.outcome,
    actor: entry.actorType,
    createdAt: entry.createdAt
  }));
}

export function buildInviteFilterDefs(sessions: CoachingSessionDto[]): CollectionFilterDef<InviteRow>[] {
  return buildCohortSessionFilterDefs(sessions) as CollectionFilterDef<InviteRow>[];
}

export function buildInvitesCollectionColumns(): CollectionColumnDef<InviteRow>[] {
  return [
    { id: "attempt", header: "Attempt", priority: 1, cell: (row) => row.attempt },
    { id: "channel", header: "Channel", priority: 2, cell: (row) => row.channel },
    {
      id: "outcome",
      header: "Outcome",
      priority: 2,
      cell: (row) => <Badge variant={row.outcome === "sent" ? "success" : "warning"}>{row.outcome}</Badge>
    },
    { id: "actor", header: "Triggered by", priority: 2, cell: (row) => row.actor },
    { id: "createdAt", header: "When", priority: 3, cell: (row) => row.createdAt }
  ];
}

export function useCohortInvitesCollection(
  rows: InviteRow[],
  sessions: CoachingSessionDto[],
  activeSessionId: string
): UseCollectionResult<InviteRow> {
  const filters = useMemo(() => buildInviteFilterDefs(sessions), [sessions]);
  const columns = useMemo(() => buildInvitesCollectionColumns(), []);
  const initialState = useMemo(
    () => cohortSessionFilterInitialState(activeSessionId),
    [activeSessionId]
  );

  return useCollection<InviteRow>({
    mode: "client",
    rows,
    columns,
    filters,
    pageSize: COHORT_TAB_PAGE_SIZE,
    initialState,
    urlSync: false,
    getRowKey: (row) => row.id
  });
}

export type { SessionScopedRow };
